import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useHistory, useLocation } from "react-router-dom";
import { usePermissions } from "react-admin";
import { MENU_STRUCTURE } from "./menuConfig";

const useStyles = makeStyles((theme) => ({
  sidebar: {
    marginTop: "1.5em",
    height: "100%",
    backgroundColor: "#f8f7f4",
    borderRight: "1px solid #e6e2da",
    boxShadow: "2px 0 8px rgba(0,0,0,0.03)",
  },

  accordion: {
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:before": {
      display: "none",
    },
    marginBottom: 4,
    position: "relative",
  },

  summary: {
    minHeight: 48,
    paddingLeft: 16,
    paddingRight: 16,
    fontFamily: "inherit",
    "& .MuiAccordionSummary-content": {
      margin: 0,
      alignItems: "center",
      justifyContent: "flex-start",
    },
  },

  sectionLabel: {
    fontWeight: 600,
    fontSize: 14,
    color: "#3f3f3f",
  },

  details: {
    padding: 0, // REMOVE default padding
  },

  list: {
    width: "100%",
    paddingTop: 4,
    paddingBottom: 8,
  },

  listItem: {
    width: "100%",
    paddingLeft: 36,
    paddingRight: 16,
    borderLeft: "4px solid transparent",
    transition: "all 0.2s ease",
    fontSize: 14,
    color: "#4a4a4a",
    "&:hover": {
      backgroundColor: "#f2eee4",
    },
  },

  activeItem: {
    backgroundColor: "#e7d8a1",
    borderLeft: "4px solid #b89c3a",
    fontWeight: 600,
    color: "#2f2f2f",
  },

  icon: {
    marginRight: 10,
    color: "#6b6b6b",
    transition: "all 0.2s ease",
  },

  activeIcon: {
    color: "#b89c3a",
  },
}));

const Menu = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { permissions } = usePermissions();
  const permissionsList = permissions ? permissions.split(",") : [];
  const open = useSelector((state) => state.admin.ui.sidebarOpen);

  const [expanded, setExpanded] = useState(null);

  const activeSection = useMemo(() => {
    return MENU_STRUCTURE.find((section) =>
      section.items.some((item) =>
        item.to === "/"
          ? location.pathname === "/"
          : location.pathname.startsWith(item.to)
      )
    )?.key;
  }, [location.pathname]);

  useEffect(() => {
    if (activeSection) {
      setExpanded(activeSection);
    }
  }, [activeSection]);

  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  return (
    <div className={classes.sidebar}>
      {MENU_STRUCTURE.map((section) => {
        if (section.show && !section.show(permissionsList)) {
          return null;
        }

        const visibleItems = section.items.filter(
          (item) => !item.show || item.show(permissionsList)
        );

        if (!visibleItems.length) return null;

        const Icon = section.icon;
        const isSectionExpanded = expanded === section.key;
        const isSectionActive = activeSection === section.key;

        return (
          <Accordion
            key={section.key}
            expanded={open && isSectionExpanded}
            onChange={open ? handleChange(section.key) : undefined}
            className={classes.accordion}
            style={{
              borderLeft:
                isSectionActive && (!isSectionExpanded || !open)
                  ? "4px solid #b89c3a"
                  : "4px solid transparent",
            }}
          >
            <AccordionSummary
              expandIcon={open ? <ExpandMoreIcon /> : null}
              className={classes.summary}
              style={{
                justifyContent: open ? "flex-start" : "center",
              }}
            >
              <Tooltip title={!open ? section.label : ""} placement="right">
                <Icon
                  style={{
                    fontSize: 22,
                    marginRight: open ? 10 : 0,
                    color: isSectionActive ? "#b89c3a" : "#6b6b6b",
                  }}
                />
              </Tooltip>

              {open && (
                <span
                  className={classes.sectionLabel}
                  style={{ color: isSectionActive ? "#b89c3a" : undefined }}
                >
                  {section.label}
                </span>
              )}
            </AccordionSummary>

            <AccordionDetails className={classes.details}>
              <List className={classes.list}>
                {visibleItems.map((item) => {
                  // isActive is determined by the following logic:
                  // 1. If the item is Home ("/"), it matches only if the current path is exactly "/".
                  // 2. For other items, it matches if the current path starts with the item's path.
                  // 3. CRUCIAL EXCEPTION: It is NOT active if there is another visible item that is "more specific" (i.e., a longer path that starts with this item's path) and ALSO matches the current location.
                  //    This prevents "parent" items (e.g., /studio) from lighting up when you are on a "child" item (e.g., /studio/config). 
                  const isActive =
                    item.to === "/"
                      ? location.pathname === "/"
                      : location.pathname.startsWith(item.to) &&
                        !visibleItems.some(
                          (other) =>
                            other.to !== item.to &&
                            other.to.startsWith(item.to) &&
                            location.pathname.startsWith(other.to)
                        );

                  return (
                    <Tooltip title={!open ? item.label : ""} placement="right">
                      <ListItem
                        button
                        key={item.to}
                        onClick={() => history.push(item.to)}
                        className={`${classes.listItem} ${
                          isActive ? classes.activeItem : ""
                        }`}
                      >
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            style: { fontSize: 14, fontWeight: isActive ? 600 : 400 },
                          }}
                        />
                      </ListItem>
                    </Tooltip>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
};

export default Menu;